import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Confirm, Dimmer, Dropdown, Form, Icon, Loader, Message, Modal, Pagination, Table } from 'semantic-ui-react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import AddCardForm from '../../components/AddCardForm';
import {
    createResidentPaymentDispute,
    cancelMembershipSubscription,
    createMembershipSubscription,
    fetchPaymentsPublicConfig,
    fetchResidentPaymentMethods,
    fetchResidentPaymentTransactions,
    getMembershipSubscription,
    isMembershipSubscriptionNotFound,
    removeResidentPaymentMethod,
    setResidentDefaultPaymentMethod,
    updateMembershipSubscription,
} from '../../services/Payments';
import { fetchFacilityDetails } from '../../services/Facilities';
import {
    PaymentPublicConfig,
    ResidentDisputeStatus,
    MembershipSubscription,
    ResidentPaymentMethod,
    ResidentPaymentTransaction,
    ResidentPaymentTransactionsResponse,
} from '../../types/Payments';
import {
    formatTransactionStatusLabel,
    normalizeTransactionStatus,
} from '../../utils/paymentTransactionStatus';
import { formatUTCDateWithTZ, userFacingDateTimeFormat } from '../../../utils/timezone';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { invalidateResidentOrderCardEligibility } from '../../hooks/useResidentOrderCardEligibility';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { getResidentDisplayFullName } from '../../utils/residentIdentity';
import './style.less';

const HISTORY_PAGE_SIZE = 10;
// The export pages through the same list call with the largest page the server allows (500) and
// stops at the same 5,000-row cap as the staff export.
const HISTORY_EXPORT_PAGE_SIZE = 500;
const HISTORY_EXPORT_MAX_ROWS = 5000;
const HISTORY_EXPORT_HEADERS = ['Date/Time', 'Source', 'Card', 'Amount', 'Status', 'Refunded', 'Dispute'];
type PaymentHistoryFilters = {
    fromDate: string;
    toDate: string;
    status: string;
    sourceType: string;
};

const DEFAULT_HISTORY_FILTERS: PaymentHistoryFilters = {
    fromDate: '',
    toDate: '',
    status: '',
    sourceType: '',
};

const TRANSACTION_STATUS_OPTIONS = [
    { key: 'all', text: 'All statuses', value: '' },
    { key: 'processing', text: 'Processing', value: 'processing' },
    { key: 'succeeded', text: 'Paid', value: 'succeeded' },
    { key: 'failed', text: 'Payment Failed', value: 'failed' },
    { key: 'skipped', text: 'Not Charged', value: 'skipped' },
    { key: 'canceled', text: 'Canceled', value: 'canceled' },
    { key: 'refunded', text: 'Refunded', value: 'refunded' },
    { key: 'partially_refunded', text: 'Partially Refunded', value: 'partially_refunded' },
    { key: 'disputed', text: 'Disputed', value: 'disputed' },
];

const TRANSACTION_SOURCE_OPTIONS = [
    { key: 'all', text: 'All sources', value: '' },
    { key: 'requestinstance', text: 'Service Request', value: 'requestinstance' },
    { key: 'activityattendance', text: 'Activity Attendance', value: 'activityattendance' },
    { key: 'menuattendance', text: 'Menu Attendance', value: 'menuattendance' },
    { key: 'membership', text: 'Membership', value: 'membership' },
];

const DISPUTE_STATUS_LABELS: Record<ResidentDisputeStatus, string> = {
    open: 'Open',
    in_review: 'In Review',
    resolved: 'Resolved',
};

type DisputeActionState = {
    canRaise: boolean;
    reason: string;
};

// Nothing was collected (or it was all returned), so there is nothing to dispute.
const DISPUTE_NOT_APPLICABLE_STATUSES = new Set(['refunded', 'failed', 'skipped', 'canceled']);

const MEMBERSHIP_STATUS_LABELS: Record<string, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Payment failed',
    unpaid: 'Payment failed',
    incomplete: 'Payment incomplete',
    incomplete_expired: 'Expired',
    canceled: 'Canceled',
    not_created: 'Not started',
    none: 'Not started',
};

const formatMembershipStatus = (status: string): string => (
    MEMBERSHIP_STATUS_LABELS[status] || status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
);

const formatShortDate = (epochMs?: number | null): string => {
    if (!epochMs || !Number.isFinite(Number(epochMs))) return '';
    return new Date(Number(epochMs)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatOrdinal = (day: number): string => {
    const tens = day % 100;
    if (tens >= 11 && tens <= 13) return `${day}th`;
    const suffix = ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] || 'th';
    return `${day}${suffix}`;
};

// Filters stay YYYY-MM-DD strings (what the transactions API takes); the picker works in local Dates.
const parseFilterDate = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const toFilterDate = (date: Date | null): string => {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

const formatBrand = (brand?: string) => {
    const normalized = String(brand || '').trim();
    if (!normalized) return 'Card';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatExpiry = (method: ResidentPaymentMethod) => {
    const month = method.card?.expMonth;
    const year = method.card?.expYear;
    if (!month || !year) return '--/--';
    const paddedMonth = String(month).padStart(2, '0');
    return `${paddedMonth}/${String(year).slice(-2)}`;
};

const getTransactionDateLabel = (transaction: ResidentPaymentTransaction, facilityTimezone: string): string => {
    if (transaction.createdAtDisplay) return transaction.createdAtDisplay;

    const fallbackDate = transaction.transactionTimeISO || transaction.createdAtIso;
    if (!fallbackDate || !facilityTimezone) return '-';

    try {
        return formatUTCDateWithTZ(fallbackDate, userFacingDateTimeFormat, facilityTimezone);
    } catch (_error) {
        return '-';
    }
};

const getTransactionSourceLabel = (transaction: ResidentPaymentTransaction): string => {
    if (transaction.sourceName) return transaction.sourceName;
    if (transaction.sourceTypeLabel) return transaction.sourceTypeLabel;
    return '-';
};

// "Visa 4242". Charges recorded before the card was stored with them have none, so they show '-'.
const getTransactionCardLabel = (transaction: ResidentPaymentTransaction): string => {
    const brand = transaction.paymentMethod?.brand;
    const last4 = transaction.paymentMethod?.last4;
    if (!brand && !last4) return '-';
    return last4 ? `${formatBrand(brand)} ${last4}` : formatBrand(brand);
};

type CardMenuRule = {
    canRemove: boolean;
    // The only card, and something depends on it: it can be replaced but not removed.
    canReplace: boolean;
    // Default card that something depends on while other cards exist: pick another default first.
    needsOtherDefault: boolean;
};

// Usage as the server sent it, or nothing: a partial usage object must not throw during render.
const hasUsage = (method: ResidentPaymentMethod): boolean => Array.isArray(method.usage?.subscriptions);

const getUsageSubscriptions = (method: ResidentPaymentMethod): { planKey: string; label: string }[] => {
    const subscriptions = method.usage?.subscriptions;
    return Array.isArray(subscriptions) ? subscriptions.filter(Boolean) : [];
};

const getUsageOpenOrderCount = (method: ResidentPaymentMethod): number => {
    const count = Number(method.usage?.openOrderCount);
    return Number.isFinite(count) && count > 0 ? count : 0;
};

// What the card menu offers, from what the card is needed for (method.usage). Without usage the
// menu offers Remove as before (the confirm then carries the old renewal warning) and the server
// enforces the rules. An unverified guest can only remove (they cannot add or change the default),
// so they keep Remove and see the server's message.
const getCardMenuRule = (method: ResidentPaymentMethod, cardCount: number, canAddOrDefaultCards: boolean): CardMenuRule => {
    if (!hasUsage(method) || !canAddOrDefaultCards) {
        return { canRemove: true, canReplace: false, needsOtherDefault: false };
    }
    const dependedOn = getUsageSubscriptions(method).length > 0 || getUsageOpenOrderCount(method) > 0;
    if (!dependedOn) {
        return { canRemove: true, canReplace: false, needsOtherDefault: false };
    }
    if (cardCount <= 1) {
        return { canRemove: false, canReplace: true, needsOtherDefault: false };
    }
    // A non-default card can go: its subscriptions move to the default and orders are not billed to it.
    if (!method.isDefault) {
        return { canRemove: true, canReplace: false, needsOtherDefault: false };
    }
    return { canRemove: false, canReplace: false, needsOtherDefault: true };
};

const getCardUsageLabel = (method: ResidentPaymentMethod): string => {
    const labels = Array.from(new Set(
        getUsageSubscriptions(method)
            .map((subscription) => (typeof subscription.label === 'string' ? subscription.label.trim() : ''))
            .filter(Boolean),
    ));
    const parts = [...labels];
    const openOrderCount = getUsageOpenOrderCount(method);
    if (openOrderCount > 0) {
        parts.push(openOrderCount === 1 ? '1 open order' : `${openOrderCount} open orders`);
    }
    return parts.join(', ');
};

// The table and the CSV export send the same filters, so the mapping lives in one place.
const buildHistoryRequestFilters = (filters: PaymentHistoryFilters) => {
    const isDisputedStatusFilter = filters.status === 'disputed';
    return {
        ...(filters.fromDate ? { from: filters.fromDate } : {}),
        ...(filters.toDate ? { to: filters.toDate } : {}),
        ...(filters.status && !isDisputedStatusFilter ? { status: filters.status } : {}),
        ...(isDisputedStatusFilter ? { residentDisputeStatus: 'all' } : {}),
        ...(filters.sourceType ? { sourceType: filters.sourceType } : {}),
    };
};

const getRefundedAmountLabel = (transaction: ResidentPaymentTransaction): string => {
    const refundedCents = Number(
        transaction.refundedAmountCents
        ?? transaction.refundedCents
        ?? transaction.refundedAmount
        ?? 0,
    );
    if (Number.isFinite(refundedCents) && refundedCents <= 0 && !transaction.refundedAmountDisplay) {
        return '-';
    }
    return transaction.refundedAmountDisplay || '-';
};

const getTransactionDisputeStatus = (transaction: ResidentPaymentTransaction): ResidentDisputeStatus | '' => {
    const status = String(transaction.residentDispute?.status || '').trim().toLowerCase();
    if (status === 'open' || status === 'in_review' || status === 'resolved') {
        return status;
    }
    return '';
};

const getDisputeBadgeLabel = (transaction: ResidentPaymentTransaction): string => {
    const disputeStatus = getTransactionDisputeStatus(transaction);
    if (!disputeStatus) return '-';
    return DISPUTE_STATUS_LABELS[disputeStatus];
};

const getDisputeActionState = (transaction: ResidentPaymentTransaction): DisputeActionState => {
    const normalizedStatus = normalizeTransactionStatus(transaction.status);
    if (normalizedStatus === 'pending' || normalizedStatus === 'processing') {
        return {
            canRaise: false,
            reason: 'Available after payment finishes',
        };
    }
    if (DISPUTE_NOT_APPLICABLE_STATUSES.has(normalizedStatus)) {
        return {
            canRaise: false,
            reason: '',
        };
    }

    const disputeStatus = getTransactionDisputeStatus(transaction);
    if (disputeStatus === 'open' || disputeStatus === 'in_review') {
        return {
            canRaise: false,
            reason: 'Dispute already in progress',
        };
    }

    return {
        canRaise: true,
        reason: '',
    };
};

const isFacilityPaymentsDisabledError = (error: unknown): boolean => {
    const typedError = (error && typeof error === 'object')
        ? (error as { message?: unknown; data?: { message?: unknown; error?: unknown; }; })
        : undefined;

    const directMessage = error instanceof Error
        ? error.message
        : String(typedError?.message || error || '');

    const nestedDataMessage = String(typedError?.data?.message || typedError?.data?.error || '');

    const combined = `${directMessage} ${nestedDataMessage}`.toLowerCase();
    return combined.includes('credit card payments are disabled for this facility')
        || combined.includes('disabled for this facility')
        || combined.includes('facility_payments_disabled');
};

const PaymentsPage = () => {
    const {
        userObject,
        setUser,
    } = useContext(UserContext);

    const {
        registrants: { list: listOfResidents, activeRegistrantsIndexes },
    } = userObject;
    const isGuestUser = userObject.userType === 'guest';
    const guestAccess = userObject.guestAccess || { active: false, verified: false, paymentsRequired: false, subscriptionRequired: false, subscriptionActive: false, subscriptionStatus: 'none', pendingReason: '' };
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];
    const residentId = activeResident?._id ? String(activeResident._id) : '';
    const facilityId = activeResident?.Facility ? String(activeResident.Facility) : '';
    const {
        showPaymentsEntry,
        canManageCards,
        loading: paymentAccessLoading,
        resolvedShowPaymentsEntry,
        resolvedCanManageCards,
    } = useNeighborsPaymentAccess();

    const [loadingMethods, setLoadingMethods] = useState<boolean>(false);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
    const [mutatingCard, setMutatingCard] = useState<boolean>(false);
    const [runtimeCardsUnavailable, setRuntimeCardsUnavailable] = useState<boolean>(false);

    const [methods, setMethods] = useState<ResidentPaymentMethod[]>([]);
    // True only after the saved-cards list loaded successfully, so an empty list means "no cards".
    const [methodsLoaded, setMethodsLoaded] = useState<boolean>(false);
    const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
    const [publishableKey, setPublishableKey] = useState<string>('');
    const [facilityTimezone, setFacilityTimezone] = useState<string>('');

    const [historyPage, setHistoryPage] = useState<number>(1);
    const [draftFilters, setDraftFilters] = useState<PaymentHistoryFilters>(DEFAULT_HISTORY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<PaymentHistoryFilters>(DEFAULT_HISTORY_FILTERS);
    const [historyData, setHistoryData] = useState<ResidentPaymentTransactionsResponse>({ items: [], TotRecords: 0 });
    const [disputeModalOpen, setDisputeModalOpen] = useState<boolean>(false);
    const [disputeTargetTransaction, setDisputeTargetTransaction] = useState<ResidentPaymentTransaction | null>(null);
    const [disputeNote, setDisputeNote] = useState<string>('');
    const [submittingDispute, setSubmittingDispute] = useState<boolean>(false);
    const [exportingHistory, setExportingHistory] = useState<boolean>(false);
    const [membership, setMembership] = useState<MembershipSubscription | null>(null);
    const [membershipLoading, setMembershipLoading] = useState<boolean>(false);
    const [membershipActionLoading, setMembershipActionLoading] = useState<'' | 'create' | 'cancel' | 'resume'>('');
    const [createMembershipConfirmOpen, setCreateMembershipConfirmOpen] = useState<boolean>(false);
    // Set when the membership could not be loaded (not the same as "no membership yet").
    const [membershipLoadError, setMembershipLoadError] = useState<string>('');
    // Blocks a repeat click on Create / Cancel / Keep before the loading state re-renders.
    const membershipActionRef = useRef(false);
    // Blocks a second Export click before the button has re-rendered.
    const exportInFlightRef = useRef(false);
    // The resident on screen ("residentId|facilityId"), or '' once the page is gone. Async work for
    // another resident (cards, export) checks it before it shows or downloads anything.
    const activeScopeRef = useRef('');
    const isMountedRef = useRef(true);
    const methodsRequestRef = useRef(0);
    const historyRequestRef = useRef(0);
    const membershipRequestRef = useRef(0);
    const [removeCardMethodId, setRemoveCardMethodId] = useState<string | null>(null);
    // Set while the Add card dialog is replacing a card that a subscription or open order depends on.
    const [replacingMethodId, setReplacingMethodId] = useState<string | null>(null);
    const [publicConfig, setPublicConfig] = useState<PaymentPublicConfig | null>(null);

    const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
        if (!publishableKey) return null;
        return loadStripe(publishableKey);
    }, [publishableKey]);

    // The export has its own state on the button, so it does not dim the whole page.
    const loading = loadingMethods || loadingHistory || mutatingCard || paymentAccessLoading || submittingDispute || membershipLoading || Boolean(membershipActionLoading);
    const defaultMethod = methods.find((method) => method.isDefault) || null;
    const filtersNotApplied = (Object.keys(DEFAULT_HISTORY_FILTERS) as (keyof PaymentHistoryFilters)[])
        .some((key) => draftFilters[key] !== appliedFilters[key]);
    const totalHistoryPages = Math.max(1, Math.ceil((historyData.TotRecords || 0) / HISTORY_PAGE_SIZE));
    const guestMembershipPaymentsRequired = Boolean(isGuestUser && guestAccess.paymentsRequired);
    const guestVerified = Boolean(guestAccess.verified);
    const guestAwaitingVerification = Boolean(isGuestUser && !guestVerified);
    // An unverified guest can still see and remove cards and cancel, but not add cards,
    // change the default or start/resume a membership (fambe enforces the same split).
    const paymentActionsAllowed = !isGuestUser || guestVerified;
    const effectiveShowPaymentsEntry = resolvedShowPaymentsEntry || guestMembershipPaymentsRequired;
    const canLoadCards = canManageCards || guestMembershipPaymentsRequired;
    const canLoadPaymentHistory = showPaymentsEntry || guestMembershipPaymentsRequired;
    const cardsAvailable = (resolvedCanManageCards || guestMembershipPaymentsRequired) && !runtimeCardsUnavailable;
    const canAddOrDefaultCards = cardsAvailable && paymentActionsAllowed;
    const membershipStatus = String(membership?.status || 'not_created').toLowerCase();
    const membershipActive = membershipStatus === 'active' || membershipStatus === 'trialing';
    const membershipPeriodEndLabel = formatShortDate(membership?.currentPeriodEnd);
    const membershipPeriodLabel = (() => {
        if (!membershipActive || !membershipPeriodEndLabel) return '';
        const startLabel = formatShortDate(membership?.currentPeriodStart);
        const range = startLabel ? `Current period ${startLabel} – ${membershipPeriodEndLabel}` : '';
        const next = membership?.cancelAtPeriodEnd ? `Ends ${membershipPeriodEndLabel}` : `Renews ${membershipPeriodEndLabel}`;
        return range ? `${range} · ${next}` : next;
    })();
    const membershipChargeLabel = (() => {
        const amountCents = Number(publicConfig?.membershipAmountCents);
        if (!Number.isFinite(amountCents) || amountCents <= 0) return '';
        const currency = String(publicConfig?.membershipCurrency || 'USD').toUpperCase();
        const interval = String(publicConfig?.membershipInterval || 'month');
        return `${(amountCents / 100).toLocaleString(undefined, { style: 'currency', currency })}/${interval}`;
    })();
    const membershipScheduleLabel = String(publicConfig?.membershipInterval || 'month') === 'month'
        ? `Charged today, then monthly on the ${formatOrdinal(new Date().getDate())}.`
        : '';
    // The only card on a membership that is still billing. When the server could not say what the
    // card is for (older backend, failed lookup), or an unverified guest cannot add another card,
    // the resident still gets the warning before removing it. Otherwise the menu hides Remove for a card
    // a plan depends on and the server refuses it.
    const removeCardMethod = methods.find((method) => method.methodId === removeCardMethodId);
    const removingOnlyCardWithActiveMembership = Boolean(
        removeCardMethod
        && methods.length === 1
        && membershipActive
        && !membership?.cancelAtPeriodEnd
        && (!hasUsage(removeCardMethod) || !paymentActionsAllowed),
    );
    const removeCardWarning = (() => {
        if (!removingOnlyCardWithActiveMembership) return '';
        if (!paymentActionsAllowed) {
            return `This is your only card. You can't add another one until staff verify your account, so your next renewal${membershipPeriodEndLabel ? ` on ${membershipPeriodEndLabel}` : ''} will fail without it. To stop being charged, cancel the membership instead of adding a card.`;
        }
        return `This is your only card. Your next renewal${membershipPeriodEndLabel ? ` on ${membershipPeriodEndLabel}` : ''} will fail without it. To stop being charged, cancel the membership instead.`;
    })();
    const setCardsRuntimeUnavailable = () => {
        setMethods([]);
        setMethodsLoaded(false);
        setPublishableKey('');
        setRuntimeCardsUnavailable(true);
    };
    const syncGuestMembershipAccess = (subscription: MembershipSubscription | null) => {
        if (!isGuestUser) return;
        const nextStatus = String(subscription?.status || 'none').toLowerCase();
        const nextActive = nextStatus === 'active' || nextStatus === 'trialing';
        setUser((prev) => {
            const verified = Boolean(prev.guestAccess?.verified);
            const active = verified && (!prev.guestAccess?.paymentsRequired || nextActive);
            return {
                ...prev,
                guestAccess: {
                    ...prev.guestAccess,
                    subscriptionActive: nextActive,
                    subscriptionStatus: nextStatus,
                    active,
                    // Same rule as fambe's guest access (neighborsHelpers).
                    pendingReason: active ? '' : (!verified ? 'verification_required' : 'subscription_required'),
                },
            };
        });
    };

    const refreshMethods = useCallback(async () => {
        if (!residentId || !facilityId || !canLoadCards) return;

        // Only the newest request for the resident on screen may change the page: a slow response
        // for the previous resident must not replace the cards, or clear the loading state.
        const requestId = ++methodsRequestRef.current;
        const scope = `${residentId}|${facilityId}`;
        const isCurrent = () => requestId === methodsRequestRef.current && activeScopeRef.current === scope;

        setLoadingMethods(true);
        try {
            const cards = await fetchResidentPaymentMethods(residentId, facilityId, true);
            if (!isCurrent()) return;
            setMethods(Array.isArray(cards) ? cards : []);
            setMethodsLoaded(true);
            setRuntimeCardsUnavailable(false);
        } catch (error) {
            if (!isCurrent()) return;
            setMethodsLoaded(false);
            if (isFacilityPaymentsDisabledError(error)) {
                setCardsRuntimeUnavailable();
                return;
            }
            throw error;
        } finally {
            if (requestId === methodsRequestRef.current) setLoadingMethods(false);
        }
    }, [residentId, facilityId, canLoadCards]);

    useEffect(() => {
        isMountedRef.current = true;
        activeScopeRef.current = `${residentId}|${facilityId}`;
        return () => {
            activeScopeRef.current = '';
            isMountedRef.current = false;
        };
    }, [residentId, facilityId]);

    const refreshHistory = useCallback(async () => {
        if (!residentId || !facilityId || !canLoadPaymentHistory) return;

        // Same rule as refreshMethods: only the newest request for the resident on screen may change
        // the page, so a slow response for the previous resident, page or filter cannot replace it.
        const requestId = ++historyRequestRef.current;
        const scope = `${residentId}|${facilityId}`;
        const isCurrent = () => requestId === historyRequestRef.current && activeScopeRef.current === scope;

        setLoadingHistory(true);
        try {
            const response = await fetchResidentPaymentTransactions({
                residentId,
                facilityId,
                page: historyPage,
                pageSize: HISTORY_PAGE_SIZE,
                ...buildHistoryRequestFilters(appliedFilters),
            });
            if (!isCurrent()) return;
            setHistoryData(response);
        } finally {
            if (requestId === historyRequestRef.current) setLoadingHistory(false);
        }
    }, [residentId, facilityId, historyPage, appliedFilters, canLoadPaymentHistory]);

    useEffect(() => {
        if (paymentAccessLoading) return;
        if (!residentId || !facilityId || !canLoadCards || runtimeCardsUnavailable) {
            setPublishableKey('');
            return;
        }

        (async () => {
            try {
                const config = await fetchPaymentsPublicConfig(facilityId);
                setPublishableKey(config?.publishableKey || '');
                setPublicConfig(config || null);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'Unable to load payments configuration');
            }
        })();
    }, [residentId, facilityId, canLoadCards, paymentAccessLoading, runtimeCardsUnavailable]);

    useEffect(() => {
        if (!canLoadCards && runtimeCardsUnavailable) {
            setRuntimeCardsUnavailable(false);
        }
    }, [canLoadCards, runtimeCardsUnavailable]);

    useEffect(() => {
        if (!facilityId) return;

        (async () => {
            try {
                const facility = await fetchFacilityDetails(facilityId);
                setFacilityTimezone(facility?.FacilityTimeZone || '');
            } catch (_error) {
                setFacilityTimezone('');
            }
        })();
    }, [facilityId]);

    useEffect(() => {
        if (paymentAccessLoading) return;
        if (!residentId || !facilityId) return;
        if (!canLoadCards || runtimeCardsUnavailable) {
            setMethods([]);
        setMethodsLoaded(false);
            return;
        }
        refreshMethods().catch((error) => {
            sendToast('error', error instanceof Error ? error.message : 'Unable to load saved cards');
        });
    }, [residentId, facilityId, refreshMethods, canLoadCards, paymentAccessLoading, runtimeCardsUnavailable]);

    useEffect(() => {
        if (paymentAccessLoading) return;
        if (!residentId || !facilityId) return;
        if (!canLoadPaymentHistory) {
            setHistoryData({ items: [], TotRecords: 0 });
            return;
        }
        refreshHistory().catch((error) => {
            sendToast('error', error instanceof Error ? error.message : 'Unable to load payment history');
        });
    }, [residentId, facilityId, refreshHistory, canLoadPaymentHistory, paymentAccessLoading]);

    // afterAction: the reload that follows Create / Cancel / Keep. The action already returned
    // the new membership, so a failed reload keeps it instead of replacing it.
    const refreshMembership = useCallback(async (afterAction: boolean = false) => {
        if (!isGuestUser || !residentId || !facilityId || !guestAccess.paymentsRequired) return;

        const requestId = ++membershipRequestRef.current;
        const scope = `${residentId}|${facilityId}`;
        const isCurrent = () => requestId === membershipRequestRef.current && activeScopeRef.current === scope;

        setMembershipLoading(true);
        try {
            const subscription = await getMembershipSubscription(residentId, facilityId);
            if (!isCurrent()) return;
            setMembership(subscription || null);
            setMembershipLoadError('');
        } catch (error) {
            if (!isCurrent()) return;
            const message = error instanceof Error ? error.message : '';
            // Live answers exactly "Membership subscription not found" when the guest has none yet.
            // "... not found for caller scope" is a permission error, so it is not matched here.
            if (isMembershipSubscriptionNotFound(error)) {
                setMembership(null);
                setMembershipLoadError('');
            } else if (!afterAction) {
                setMembershipLoadError(message || 'Unable to load membership');
            }
        } finally {
            if (requestId === membershipRequestRef.current) setMembershipLoading(false);
        }
    }, [facilityId, guestAccess.paymentsRequired, isGuestUser, residentId]);

    useEffect(() => {
        setMembershipLoadError('');
        refreshMembership().catch((error) => {
            sendToast('error', error instanceof Error ? error.message : 'Unable to load membership');
        });
    }, [refreshMembership]);

    const handleSetDefault = async (methodId: string) => {
        try {
            setMutatingCard(true);
            await setResidentDefaultPaymentMethod(residentId, methodId, facilityId);
            invalidateResidentOrderCardEligibility(residentId, facilityId);
            await refreshMethods();
            sendToast('success', 'Default card updated');
        } catch (error) {
            if (isFacilityPaymentsDisabledError(error)) {
                setCardsRuntimeUnavailable();
                sendToast('error', 'Card management is currently unavailable because this facility has disabled card payments.');
                return;
            }
            sendToast('error', error instanceof Error ? error.message : 'Unable to set default card');
        } finally {
            setMutatingCard(false);
        }
    };

    const handleReplaceClick = (methodId: string) => {
        setReplacingMethodId(methodId);
        setAddModalOpen(true);
    };

    const closeAddCardModal = () => {
        setAddModalOpen(false);
        setReplacingMethodId(null);
    };

    const handleRemove = async (methodId: string) => {
        try {
            setMutatingCard(true);
            await removeResidentPaymentMethod(residentId, methodId, facilityId);
            invalidateResidentOrderCardEligibility(residentId, facilityId);
            await refreshMethods();
            sendToast('success', 'Card removed');
        } catch (error) {
            if (isFacilityPaymentsDisabledError(error)) {
                setCardsRuntimeUnavailable();
                sendToast('error', 'Card management is currently unavailable because this facility has disabled card payments.');
                return;
            }
            sendToast('error', error instanceof Error ? error.message : 'Unable to remove card');
        } finally {
            setMutatingCard(false);
        }
    };

    const handleResetFilters = () => {
        setDraftFilters(DEFAULT_HISTORY_FILTERS);
        setAppliedFilters(DEFAULT_HISTORY_FILTERS);
        setHistoryPage(1);
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setHistoryPage(1);
    };

    // Downloads every row matching the applied filters (not just the visible page), in the order
    // the table shows them. Uses the same scoped list call as the table, so nothing extra is exposed.
    // A file is only written when every row was collected: a partial file would look complete.
    const handleExportCsv = async () => {
        if (!residentId || !facilityId || exportInFlightRef.current) return;
        exportInFlightRef.current = true;

        const scope = `${residentId}|${facilityId}`;
        // The resident switcher stays usable during the export; leaving the page or switching
        // resident abandons it.
        const isStillHere = () => isMountedRef.current && activeScopeRef.current === scope;

        setExportingHistory(true);
        try {
            const requestFilters = buildHistoryRequestFilters(appliedFilters);
            const exportedItems: ResidentPaymentTransaction[] = [];
            const seenTransactionIds = new Set<string>();
            let totalRecords = 0;
            for (let page = 1; exportedItems.length < HISTORY_EXPORT_MAX_ROWS; page += 1) {
                // eslint-disable-next-line no-await-in-loop
                const response = await fetchResidentPaymentTransactions({
                    residentId,
                    facilityId,
                    page,
                    pageSize: HISTORY_EXPORT_PAGE_SIZE,
                    ...requestFilters,
                });
                if (!isStillHere()) return;
                const responseTotal = Number(response.TotRecords);
                if (!Number.isFinite(responseTotal)) {
                    throw new Error('The export could not be completed because the number of transactions was not returned.');
                }
                totalRecords = responseTotal;
                if (totalRecords > HISTORY_EXPORT_MAX_ROWS) {
                    sendToast('warn', 'Export exceeds 5,000 rows. Narrow the dates or filters and try again.');
                    return;
                }
                const pageItems = Array.isArray(response.items) ? response.items : [];
                if (pageItems.length === 0) break;
                const collectedBefore = exportedItems.length;
                // Paging is by offset, so a charge added during the export can shift rows onto the next page.
                pageItems.forEach((item) => {
                    const transactionId = String(item.transactionId || '');
                    if (transactionId) {
                        if (seenTransactionIds.has(transactionId)) return;
                        seenTransactionIds.add(transactionId);
                    }
                    exportedItems.push(item);
                });
                // A page of rows already collected adds nothing, and the next page could repeat it
                // forever. Stop here: the check below reports the export as incomplete.
                if (exportedItems.length === collectedBefore) break;
                if (exportedItems.length >= totalRecords) break;
            }

            if (exportedItems.length !== totalRecords) {
                sendToast('error', `The export is incomplete (${exportedItems.length} of ${totalRecords} transactions), so no file was created. Please try again.`);
                return;
            }
            if (exportedItems.length === 0) {
                sendToast('warn', 'No transactions to export for the selected filters.');
                return;
            }

            // Empty cell instead of '-' so spreadsheets do not see a stray dash.
            const blankIfDash = (value: string) => (value === '-' ? '' : value);
            const csv = buildCsv(
                HISTORY_EXPORT_HEADERS,
                exportedItems.map((transaction) => [
                    blankIfDash(getTransactionDateLabel(transaction, facilityTimezone)),
                    blankIfDash(getTransactionSourceLabel(transaction)),
                    blankIfDash(getTransactionCardLabel(transaction)),
                    blankIfDash(transaction.amountDisplay || ''),
                    formatTransactionStatusLabel(transaction.status),
                    blankIfDash(getRefundedAmountLabel(transaction)),
                    blankIfDash(getDisputeBadgeLabel(transaction)),
                ]),
                // Only the Source column holds free text staff can type (service and dish names).
                { guardFormulaColumns: [HISTORY_EXPORT_HEADERS.indexOf('Source')] },
            );
            const residentNamePart = getResidentDisplayFullName(activeResident)
                .replace(/[^A-Za-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            downloadCsv(
                `payment-history-${residentNamePart ? `${residentNamePart}-` : ''}${toFilterDate(new Date())}.csv`,
                csv,
            );
        } catch (error) {
            if (isStillHere()) {
                sendToast('error', error instanceof Error ? error.message : 'Unable to export payment history');
            }
        } finally {
            exportInFlightRef.current = false;
            if (isMountedRef.current) setExportingHistory(false);
        }
    };

    const closeDisputeModal = () => {
        setDisputeModalOpen(false);
        setDisputeTargetTransaction(null);
        setDisputeNote('');
    };

    const handleRaiseDisputeClick = (transaction: ResidentPaymentTransaction) => {
        const disputeAction = getDisputeActionState(transaction);
        if (!disputeAction.canRaise) {
            sendToast('warn', disputeAction.reason || 'Dispute is not available for this transaction');
            return;
        }
        setDisputeTargetTransaction(transaction);
        setDisputeNote('');
        setDisputeModalOpen(true);
    };

    const handleSubmitDispute = async () => {
        if (!disputeTargetTransaction || !disputeTargetTransaction.transactionId) {
            sendToast('error', 'Please choose a transaction to dispute');
            return;
        }
        if (!disputeNote.trim()) {
            sendToast('warn', 'Please enter a dispute note');
            return;
        }

        try {
            setSubmittingDispute(true);
            await createResidentPaymentDispute(
                residentId,
                facilityId,
                String(disputeTargetTransaction.transactionId),
                disputeNote,
            );
            sendToast('success', 'Dispute submitted');
            closeDisputeModal();
            await refreshHistory();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to submit dispute');
        } finally {
            setSubmittingDispute(false);
        }
    };

    const isMembershipSubscriptionActive = (subscription: MembershipSubscription | null) => {
        const status = String(subscription?.status || '').toLowerCase();
        return status === 'active' || status === 'trialing';
    };

    // Shows the bank's approval popup (3DS) for the first membership payment.
    // Returns an error message, or '' when the bank approved it.
    const approveMembershipPayment = async (clientSecret: string): Promise<string> => {
        const stripeClient = stripePromise ? await stripePromise : null;
        if (!stripeClient) return 'Card payments are not available right now. Please try again later.';
        try {
            const result = await stripeClient.confirmCardPayment(clientSecret);
            if (result.error) return result.error.message || 'Your bank did not approve this payment.';
            return '';
        } catch (error) {
            return error instanceof Error ? error.message : 'Your bank did not approve this payment.';
        }
    };

    const handleCreateMembershipConfirmed = async () => {
        setCreateMembershipConfirmOpen(false);
        if (membershipActionRef.current) return;
        membershipActionRef.current = true;
        try {
            setMembershipActionLoading('create');
            const created = await createMembershipSubscription(residentId, facilityId);
            let subscription = created;
            let approvalError = '';
            const approvalSecret = created.paymentAction?.clientSecret || '';
            if (approvalSecret && !isMembershipSubscriptionActive(created)) {
                approvalError = await approveMembershipPayment(approvalSecret);
                if (!approvalError) {
                    subscription = (await getMembershipSubscription(residentId, facilityId).catch(() => null)) || created;
                }
            }
            setMembership(subscription);
            setMembershipLoadError('');
            syncGuestMembershipAccess(subscription);
            await refreshMembership(true);
            if (isMembershipSubscriptionActive(subscription)) {
                sendToast('success', 'Membership created');
            } else if (approvalError) {
                sendToast('error', `${approvalError} Your membership is not active yet. You can try again with the same or a different card.`);
            } else if (approvalSecret) {
                sendToast('warn', 'Payment approved. Your membership will be active in a moment. Reload this page if it still shows pending.');
            } else {
                sendToast('warn', 'Your membership is not active yet because the first payment did not go through. Please check your card or try a different one.');
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to create membership');
        } finally {
            setMembershipActionLoading('');
            membershipActionRef.current = false;
        }
    };

    const handleCancelMembership = async () => {
        if (membershipActionRef.current) return;
        membershipActionRef.current = true;
        try {
            setMembershipActionLoading('cancel');
            const subscription = await cancelMembershipSubscription(residentId, facilityId);
            setMembership(subscription);
            syncGuestMembershipAccess(subscription);
            await refreshMembership(true);
            sendToast('success', 'Membership will cancel at period end');
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to cancel membership');
        } finally {
            setMembershipActionLoading('');
            membershipActionRef.current = false;
        }
    };

    const handleResumeMembership = async () => {
        if (membershipActionRef.current) return;
        membershipActionRef.current = true;
        try {
            setMembershipActionLoading('resume');
            const subscription = await updateMembershipSubscription(residentId, facilityId, {
                cancelAtPeriodEnd: false,
            });
            setMembership(subscription);
            syncGuestMembershipAccess(subscription);
            await refreshMembership(true);
            if (isMembershipSubscriptionActive(subscription)) {
                sendToast('success', 'Membership will remain active');
            } else {
                sendToast('warn', 'Your bank needs to confirm this payment before the membership is active. Check for a message from your bank, then reload this page.');
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to update membership');
        } finally {
            setMembershipActionLoading('');
            membershipActionRef.current = false;
        }
    };

    if (!residentId || !facilityId) {
        return <Message warning content="Resident details are missing. Please contact support." />;
    }

    return (
        <div className="neighbors-payments-page" style={{ position: 'relative' }}>
            <Dimmer active={loading} inverted>
                <Loader active={loading} />
            </Dimmer>

            <div className="neighbors-payments-head">
                <h1>Payments</h1>
                <p>Manage your payment methods and review past charges.</p>
            </div>

            {!effectiveShowPaymentsEntry && (
                <Message
                    warning
                    header="Payments are currently unavailable"
                    content="This facility is not accepting card payments and no payment history is available."
                />
            )}

            {isGuestUser && guestAccess.paymentsRequired && (
                <div className="neighbors-payments-panel neighbors-membership-panel">
                    <div className="neighbors-payments-panel-header">
                        <h2>Guest membership</h2>
                    </div>
                    <p className="neighbors-membership-copy">
                        Membership is required for guest access at this facility.
                        {membershipChargeLabel ? ` Billing: ${membershipChargeLabel}.` : ''}
                    </p>
                    {membershipLoadError ? (
                        <Message warning>
                            <Message.Header>Couldn't load your membership</Message.Header>
                            <p>We could not check your membership right now. Retry before creating one.</p>
                            <Button
                                type="button"
                                size="small"
                                className="neighbors-payments-btn-ghost"
                                onClick={() => { refreshMembership().catch(() => {}); }}
                                loading={membershipLoading}
                                disabled={membershipLoading}
                            >
                                Retry
                            </Button>
                        </Message>
                    ) : (
                        <div className="neighbors-membership-status">
                            <b>Status:</b> {formatMembershipStatus(membershipStatus)}
                            {membershipPeriodLabel && (
                                <div className="neighbors-membership-period">{membershipPeriodLabel}</div>
                            )}
                        </div>
                    )}
                    {!guestVerified && membershipActive && (
                        <Message warning content="Your account is awaiting staff re-verification. Your membership stays active until you cancel." />
                    )}
                    {!guestVerified && !membershipActive && (
                        <Message warning content="Staff verification is required before you can add a card or create a membership." />
                    )}
                    {guestVerified && !defaultMethod && !membershipActive && (
                        <Message info content="Add a default card before creating membership." />
                    )}
                    {!membershipActive && guestVerified && (
                        <Button
                            className="neighbors-payments-btn-primary"
                            primary
                            onClick={() => setCreateMembershipConfirmOpen(true)}
                            disabled={!defaultMethod || Boolean(membershipActionLoading) || Boolean(membershipLoadError)}
                            loading={membershipActionLoading === 'create'}
                        >
                            Create membership
                        </Button>
                    )}
                    {membershipActive && !membership?.cancelAtPeriodEnd && (
                        <Button
                            className="neighbors-payments-btn-ghost"
                            basic
                            color="red"
                            onClick={handleCancelMembership}
                            disabled={Boolean(membershipActionLoading)}
                            loading={membershipActionLoading === 'cancel'}
                        >
                            Cancel at period end
                        </Button>
                    )}
                    {membershipActive && membership?.cancelAtPeriodEnd && guestVerified && (
                        <Button
                            className="neighbors-payments-btn-primary"
                            primary
                            onClick={handleResumeMembership}
                            disabled={Boolean(membershipActionLoading)}
                            loading={membershipActionLoading === 'resume'}
                        >
                            Keep membership active
                        </Button>
                    )}
                </div>
            )}

            {effectiveShowPaymentsEntry && cardsAvailable && (
                <div className="neighbors-payments-panel neighbors-payments-cards-panel">
                    <div className="neighbors-payments-panel-header">
                        <div>
                            <h2>Saved cards</h2>
                            <div className="neighbors-payments-panel-hint">Your default card is used for monthly billing.</div>
                        </div>
                        {canAddOrDefaultCards && (
                            <Button className="neighbors-payments-btn-primary" primary onClick={() => setAddModalOpen(true)}>
                                <Icon name="plus" />
                                Add card
                            </Button>
                        )}
                    </div>

                    <div className="neighbors-payments-cards-grid">
                        {methods.length === 0 && (
                            <div className="neighbors-payments-empty-hint">No saved cards yet.</div>
                        )}

                        {methods.map((method) => {
                            const cardRule = getCardMenuRule(method, methods.length, canAddOrDefaultCards);
                            const cardUsageLabel = getCardUsageLabel(method);
                            return (
                            <div
                                key={method.methodId}
                                className={`neighbors-payments-ccard${method.isDefault ? ' default' : ''}`}
                            >
                                <div className="neighbors-payments-ccard-top">
                                    <div className="neighbors-payments-ccard-top-left">
                                        <span className="neighbors-payments-ccard-chip" />
                                        {method.isDefault && <span className="neighbors-payments-ccard-badge">Default</span>}
                                    </div>
                                    <Dropdown
                                        icon={null}
                                        className="neighbors-payments-ccard-menu"
                                        direction="left"
                                        trigger={(
                                            <span className="neighbors-payments-ccard-menubtn" aria-label="Card options">
                                                <Icon name="ellipsis vertical" />
                                            </span>
                                        )}
                                    >
                                        <Dropdown.Menu>
                                            {!method.isDefault && canAddOrDefaultCards && (
                                                <Dropdown.Item onClick={() => handleSetDefault(method.methodId)}>
                                                    <Icon name="star outline" />
                                                    Set as default
                                                </Dropdown.Item>
                                            )}
                                            {cardRule.canReplace && (
                                                <Dropdown.Item onClick={() => handleReplaceClick(method.methodId)}>
                                                    <Icon name="exchange" />
                                                    Replace card
                                                </Dropdown.Item>
                                            )}
                                            {cardRule.canRemove && (
                                                <Dropdown.Item
                                                    className="danger"
                                                    onClick={() => setRemoveCardMethodId(method.methodId)}
                                                >
                                                    <Icon name="trash alternate outline" />
                                                    Remove
                                                </Dropdown.Item>
                                            )}
                                            {cardRule.needsOtherDefault && (
                                                <Dropdown.Item disabled>
                                                    Make another card your default to remove this one
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>

                                <div className="neighbors-payments-ccard-num">
                                    •••• •••• •••• {method.card?.last4 || '----'}
                                </div>

                                <div className="neighbors-payments-ccard-meta">
                                    <div>
                                        <div className="lbl">Brand</div>
                                        <div className="val">{formatBrand(method.card?.brand)}</div>
                                    </div>
                                    <div>
                                        <div className="lbl">Expires</div>
                                        <div className="val">{formatExpiry(method)}</div>
                                    </div>
                                </div>
                                {cardUsageLabel && (
                                    <div className="neighbors-payments-ccard-usage">Pays for: {cardUsageLabel}</div>
                                )}
                            </div>
                            );
                        })}

                        {canAddOrDefaultCards && (
                            <div
                                className="neighbors-payments-add-tile"
                                role="button"
                                tabIndex={0}
                                onClick={() => setAddModalOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setAddModalOpen(true);
                                    }
                                }}
                            >
                                <span className="circ"><Icon name="plus" /></span>
                                Add a new card
                            </div>
                        )}
                    </div>
                </div>
            )}

            {effectiveShowPaymentsEntry && !cardsAvailable && !runtimeCardsUnavailable && !guestAwaitingVerification && (
                <Message
                    warning
                    header="Card management is currently unavailable"
                    content="This facility is not accepting new card payments right now. You can still review your payment history below."
                />
            )}

            {effectiveShowPaymentsEntry && runtimeCardsUnavailable && (
                <Message
                    warning
                    header="Card management is temporarily unavailable"
                    content="This facility has disabled card payments. You can still review payment history below."
                />
            )}

            {effectiveShowPaymentsEntry && (
                <div className="neighbors-payments-panel neighbors-payments-history-panel">
                    <div className="neighbors-payments-panel-header">
                        <h3>Payment History</h3>
                    </div>

                    <div className="neighbors-payments-filter-wrap">
                        <div className="neighbors-payments-filter-caption">Filters</div>
                        <Form className="neighbors-payments-history-filters">
                            <div className="neighbors-payments-filter-grid">
                                <Form.Field className="neighbors-payments-filter-field">
                                    <label htmlFor="neighbors-payments-from">From</label>
                                    <DatePicker
                                        id="neighbors-payments-from"
                                        selected={parseFilterDate(draftFilters.fromDate)}
                                        onChange={(date) => {
                                            const value = toFilterDate(date instanceof Date ? date : null);
                                            setDraftFilters((prev) => ({ ...prev, fromDate: value }));
                                        }}
                                        selectsStart
                                        startDate={parseFilterDate(draftFilters.fromDate)}
                                        endDate={parseFilterDate(draftFilters.toDate)}
                                        maxDate={parseFilterDate(draftFilters.toDate) || new Date()}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        isClearable
                                        autoComplete="off"
                                    />
                                </Form.Field>
                                <Form.Field className="neighbors-payments-filter-field">
                                    <label htmlFor="neighbors-payments-to">To</label>
                                    <DatePicker
                                        id="neighbors-payments-to"
                                        selected={parseFilterDate(draftFilters.toDate)}
                                        onChange={(date) => {
                                            const value = toFilterDate(date instanceof Date ? date : null);
                                            setDraftFilters((prev) => ({ ...prev, toDate: value }));
                                        }}
                                        selectsEnd
                                        startDate={parseFilterDate(draftFilters.fromDate)}
                                        endDate={parseFilterDate(draftFilters.toDate)}
                                        minDate={parseFilterDate(draftFilters.fromDate)}
                                        maxDate={new Date()}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        isClearable
                                        autoComplete="off"
                                    />
                                </Form.Field>
                                <Form.Select
                                    className="neighbors-payments-filter-field"
                                    label="Status"
                                    placeholder="All statuses"
                                    options={TRANSACTION_STATUS_OPTIONS}
                                    value={draftFilters.status}
                                    onChange={(_e, data) => setDraftFilters((prev) => ({ ...prev, status: String(data.value || '') }))}
                                />
                                <Form.Select
                                    className="neighbors-payments-filter-field"
                                    label="Source"
                                    placeholder="All sources"
                                    options={TRANSACTION_SOURCE_OPTIONS}
                                    value={draftFilters.sourceType}
                                    onChange={(_e, data) => setDraftFilters((prev) => ({ ...prev, sourceType: String(data.value || '') }))}
                                />
                                <div className="neighbors-payments-history-actions">
                                    <Button className="neighbors-payments-btn-primary" primary size="small" type="button" onClick={handleApplyFilters}>
                                        Apply filters
                                    </Button>
                                    <Button className="neighbors-payments-clear-btn" size="small" type="button" onClick={handleResetFilters}>Reset</Button>
                                    {/* The export uses the applied filters, so edits in the boxes above wait for Apply. */}
                                    <span title={filtersNotApplied ? 'Apply filters to export' : undefined}>
                                        <Button
                                            className="neighbors-payments-clear-btn"
                                            size="small"
                                            type="button"
                                            icon="download"
                                            content={exportingHistory ? 'Exporting...' : 'Export CSV'}
                                            disabled={exportingHistory || historyData.items.length === 0 || filtersNotApplied}
                                            onClick={handleExportCsv}
                                        />
                                    </span>
                                </div>
                            </div>
                        </Form>
                    </div>

                    <div className="neighbors-payments-summary-row">
                        <div className="neighbors-payments-summary-item"><b>Transactions</b><span>{historyData.summary?.transactionCount ?? historyData.TotRecords ?? 0}</span></div>
                        <div className="neighbors-payments-summary-item"><b>Total</b><span>{historyData.summary?.totalAmountDisplay || '$0.00'}</span></div>
                        <div className="neighbors-payments-summary-item"><b>Refunded</b><span>{historyData.summary?.refundedAmountDisplay || '$0.00'}</span></div>
                    </div>

                    {historyData.warnings && historyData.warnings.length > 0 && (
                        <Message warning content={historyData.warnings[0]} />
                    )}

                    {historyData.items.length === 0 ? (
                        <Message info content="No transactions found for selected filters." />
                    ) : (
                        <>
                            <div className="neighbors-payments-history-table-wrap">
                            <Table className="neighbors-payments-history-table">
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Date/Time</Table.HeaderCell>
                                        <Table.HeaderCell>Source</Table.HeaderCell>
                                        <Table.HeaderCell>Card</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
                                        <Table.HeaderCell>Status</Table.HeaderCell>
                                        <Table.HeaderCell>Refunded</Table.HeaderCell>
                                        <Table.HeaderCell>Dispute</Table.HeaderCell>
                                        <Table.HeaderCell collapsing>Action</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {historyData.items.map((transaction) => {
                                        const normalizedStatus = normalizeTransactionStatus(transaction.status);
                                        const disputeStatus = getTransactionDisputeStatus(transaction);
                                        const disputeAction = getDisputeActionState(transaction);
                                        return (
                                            <Table.Row key={transaction.transactionId}>
                                                <Table.Cell>{getTransactionDateLabel(transaction, facilityTimezone)}</Table.Cell>
                                                <Table.Cell>{getTransactionSourceLabel(transaction)}</Table.Cell>
                                                <Table.Cell>{getTransactionCardLabel(transaction)}</Table.Cell>
                                                <Table.Cell textAlign="right" className="neighbors-payments-amt">{transaction.amountDisplay || '-'}</Table.Cell>
                                                <Table.Cell>
                                                    <span className={`neighbors-payments-status-pill status-${normalizedStatus}`}>
                                                        <span className="dot" />
                                                        {formatTransactionStatusLabel(transaction.status)}
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell>{getRefundedAmountLabel(transaction)}</Table.Cell>
                                                <Table.Cell>
                                                    {disputeStatus ? (
                                                        <span className={`neighbors-payments-dispute-pill dispute-${disputeStatus}`}>
                                                            <span className="dot" />
                                                            {getDisputeBadgeLabel(transaction)}
                                                        </span>
                                                    ) : '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {disputeAction.canRaise ? (
                                                        <Button
                                                            size="mini"
                                                            basic
                                                            color="orange"
                                                            onClick={() => handleRaiseDisputeClick(transaction)}
                                                        >
                                                            Raise dispute
                                                        </Button>
                                                    ) : (
                                                        <span className="neighbors-payments-dispute-disabled-reason">
                                                            {disputeAction.reason || '-'}
                                                        </span>
                                                    )}
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                            </div>

                            {totalHistoryPages > 1 && (
                                <div className="neighbors-payments-history-pagination">
                                    <Pagination
                                        activePage={historyPage}
                                        totalPages={totalHistoryPages}
                                        onPageChange={(_e, data) => {
                                            setHistoryPage(Number(data.activePage || 1));
                                        }}
                                        ellipsisItem={null}
                                        firstItem={null}
                                        lastItem={null}
                                        siblingRange={1}
                                        boundaryRange={0}
                                        size="mini"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <Confirm
                className="neighbors-payments-remove-card-confirm"
                open={Boolean(removeCardMethodId)}
                onCancel={() => setRemoveCardMethodId(null)}
                onConfirm={() => {
                    const methodId = removeCardMethodId;
                    setRemoveCardMethodId(null);
                    if (methodId) handleRemove(methodId);
                }}
                content={removeCardWarning || 'Remove this card?'}
                confirmButton={removeCardWarning ? 'Remove anyway' : 'OK'}
            />

            <Modal className="neighbors-payments-modal" open={createMembershipConfirmOpen} onClose={() => setCreateMembershipConfirmOpen(false)} size="small">
                <Modal.Header>Confirm membership</Modal.Header>
                <Modal.Content>
                    <p>You are about to create a guest membership at this facility.</p>
                    {membershipChargeLabel && (
                        <p><b>Billing:</b> {membershipChargeLabel}</p>
                    )}
                    {defaultMethod && (
                        <p>
                            <b>Card to be charged:</b>{' '}
                            {formatBrand(defaultMethod.card?.brand)} ending in {defaultMethod.card?.last4 || '----'}
                            {' '}(expires {formatExpiry(defaultMethod)})
                        </p>
                    )}
                    {membershipScheduleLabel && <p>{membershipScheduleLabel}</p>}
                    <p>Your membership will begin immediately and renew automatically. You can cancel at any time.</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button className="neighbors-payments-btn-ghost" type="button" onClick={() => setCreateMembershipConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button className="neighbors-payments-btn-primary" primary type="button" onClick={handleCreateMembershipConfirmed}>
                        Confirm & create
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal className="neighbors-payments-modal" open={disputeModalOpen} onClose={closeDisputeModal} size="small">
                <Modal.Header>Raise payment dispute</Modal.Header>
                <Modal.Content>
                    <p className="neighbors-payments-dispute-modal-subtitle">
                        Tell staff what looks incorrect with this payment.
                    </p>
                    <Form>
                        <Form.TextArea
                            label="Dispute note"
                            required
                            rows={5}
                            value={disputeNote}
                            onChange={(_event, data) => setDisputeNote(String(data.value || ''))}
                            placeholder="Describe the issue with this payment..."
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button className="neighbors-payments-btn-ghost" type="button" onClick={closeDisputeModal} disabled={submittingDispute}>
                        Cancel
                    </Button>
                    <Button
                        className="neighbors-payments-btn-primary"
                        primary
                        type="button"
                        loading={submittingDispute}
                        disabled={submittingDispute || !disputeNote.trim()}
                        onClick={handleSubmitDispute}
                    >
                        Submit dispute
                    </Button>
                </Modal.Actions>
            </Modal>

            {canAddOrDefaultCards && (
                <Modal
                    className="neighbors-payments-modal"
                    open={addModalOpen}
                    // Closing while the card is being saved would clear the card being replaced.
                    closeOnEscape={!mutatingCard}
                    closeOnDimmerClick={!mutatingCard}
                    onClose={mutatingCard ? undefined : closeAddCardModal}
                    size="small"
                >
                    <Modal.Header>{replacingMethodId ? 'Replace card' : 'Add a new card'}</Modal.Header>
                    <Modal.Content>
                        {!stripePromise ? (
                            <Message warning content="Payment form is unavailable right now. Please try again in a moment." />
                        ) : (
                            <Elements stripe={stripePromise}>
                                <AddCardForm
                                    residentId={residentId}
                                    facilityId={facilityId}
                                    shouldMakeDefault={Boolean(replacingMethodId) || (methodsLoaded && methods.length === 0)}
                                    replacingMethodId={replacingMethodId || undefined}
                                    onCancel={closeAddCardModal}
                                    onSubmittingChange={setMutatingCard}
                                    onSuccess={async () => {
                                        // Saved: close first, so a failed refresh never leaves the form open on the same card.
                                        closeAddCardModal();
                                        invalidateResidentOrderCardEligibility(residentId, facilityId);
                                        try {
                                            await refreshMethods();
                                        } catch (_error) {
                                            sendToast('warn', 'New card saved. Refresh to see it.');
                                        }
                                    }}
                                />
                            </Elements>
                        )}
                    </Modal.Content>
                </Modal>
            )}
        </div>
    );
};

export default PaymentsPage;
